<script lang="ts" setup>
import { ref, watch, computed, onMounted } from "vue";
import { subscriptionsApi } from "@/services/subscriptions";
import api from "@/services/api";
import { useProducts } from "@/queries/products";

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "subscribed"): void;
}>();

const props = defineProps<{
  show: boolean;
}>();

const { products } = useProducts();

// Only products with a published version can be subscribed to (the backend rejects
// subscriptions to a product with no published version).
const subscribableProducts = computed(() =>
  products.value.filter((p) => p.hasPublishedVersion)
);
const unsubscribableCount = computed(
  () => products.value.length - subscribableProducts.value.length
);

const selectedOrgId = ref("");
const selectedProductId = ref("");
const startDate = ref("");
const endDate = ref("");
const hasEndDate = ref(false);
const loading = ref(false);
const error = ref("");
const organizations = ref<any[]>([]);

// Set default start date to today
const today = computed(() => {
  const date = new Date();
  return date.toISOString().split("T")[0];
});

onMounted(async () => {
  // products loaded reactively by useProducts()
});

watch(() => props.show, async (newValue) => {
  if (newValue) {
    // Reset form when modal opens
    selectedOrgId.value = "";
    selectedProductId.value = "";
    startDate.value = today.value;
    endDate.value = "";
    hasEndDate.value = false;
    error.value = "";

    // Load available organizations
    await loadOrganizations();
  }
});

async function loadOrganizations() {
  try {
    const response = await api.get("/orgs?take=100");
    if (response.ok) {
      organizations.value = await response.json();
    }
  } catch (err: any) {
    console.error("Failed to load organizations:", err);
  }
}


async function addSubscriber() {
  if (!selectedOrgId.value) {
    error.value = "Please select an organization";
    return;
  }

  if (!selectedProductId.value) {
    error.value = "Please select a product";
    return;
  }

  if (!startDate.value) {
    error.value = "Please select a start date";
    return;
  }

  if (hasEndDate.value && endDate.value && endDate.value <= startDate.value) {
    error.value = "End date must be after start date";
    return;
  }

  try {
    loading.value = true;
    error.value = "";

    await subscriptionsApi.createSubscription({
      orgId: selectedOrgId.value,
      productId: selectedProductId.value,
      startDate: startDate.value,
      endDate: hasEndDate.value && endDate.value ? endDate.value : null,
    });

    emit("subscribed");
    emit("update:show", false);
  } catch (err: any) {
    error.value = err.message || "Failed to add subscriber";
  } finally {
    loading.value = false;
  }
}

function close() {
  emit("update:show", false);
}
</script>

<template>
  <div
    v-if="show"
    class="modal show d-block"
    tabindex="-1"
    @click.self="close"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-building me-2"></i>
            Add Subscriber
          </h2>
          <button
            type="button"
            class="btn-close"
            @click="close"
            :disabled="loading"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <div class="mb-3">
            <label for="productSelect" class="form-label">Select Product</label>
            <select
              id="productSelect"
              v-model="selectedProductId"
              class="form-select"
              :disabled="loading || subscribableProducts.length === 0"
            >
              <option value="">Choose a product...</option>
              <option
                v-for="product in subscribableProducts"
                :key="product.id"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
            <small class="form-text text-muted">
              Only products with a current published version are listed.
              <span v-if="unsubscribableCount > 0">
                {{ unsubscribableCount }} product{{ unsubscribableCount === 1 ? "" : "s" }}
                hidden because no version has been promoted yet.
              </span>
            </small>
            <div
              v-if="subscribableProducts.length === 0"
              class="alert alert-warning small mt-2 mb-0"
            >
              <i class="bi bi-exclamation-triangle me-2"></i>
              None of your products have a current published version yet — promote a version
              before adding subscribers.
            </div>
          </div>

          <div class="mb-3">
            <label for="orgSelect" class="form-label">Select Organization</label>
            <select
              id="orgSelect"
              v-model="selectedOrgId"
              class="form-select"
              :disabled="loading"
            >
              <option value="">Choose an organization...</option>
              <option
                v-for="org in organizations"
                :key="org.orgId"
                :value="org.orgId"
              >
                {{ org.orgName }}
              </option>
            </select>
            <small class="form-text text-muted">
              Select the organization to subscribe to the product
            </small>
          </div>

          <div class="mb-3">
            <label for="startDate" class="form-label">Start Date</label>
            <input
              id="startDate"
              v-model="startDate"
              type="date"
              class="form-control"
              :disabled="loading"
              :min="today"
            />
            <small class="form-text text-muted">
              The date when the subscription becomes active
            </small>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input
                id="hasEndDate"
                v-model="hasEndDate"
                type="checkbox"
                class="form-check-input"
                :disabled="loading"
              />
              <label class="form-check-label" for="hasEndDate">
                Set an end date
              </label>
            </div>
          </div>

          <div v-if="hasEndDate" class="mb-3">
            <label for="endDate" class="form-label">End Date</label>
            <input
              id="endDate"
              v-model="endDate"
              type="date"
              class="form-control"
              :disabled="loading"
              :min="startDate"
            />
            <small class="form-text text-muted">
              Leave blank for indefinite subscription
            </small>
          </div>

          <div class="alert alert-info mb-0">
            <i class="bi bi-info-circle me-2"></i>
            The organization will be able to create projects using this product during
            the subscription period.
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="close"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="addSubscriber"
            :disabled="loading || !selectedOrgId || !selectedProductId || !startDate"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="bi bi-plus-circle me-2"></i>
            Add Subscriber
          </button>
        </div>
      </div>
    </div>
  </div>
<div v-if="show" class="modal-backdrop fade show" @click="close"></div>
</template>

<style scoped lang="scss">
.modal.show {
  display: block;
}
</style>
